import React, { useEffect, useRef, useState } from 'react';
import styles from './FundingHistory.module.scss';
import { fetchFundingHistoryData } from '../../../../../../store/dashboard/overview/overview_apiHelper';
import { useParams } from 'react-router-dom';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { downloadFile } from '../../../../../Company/UploadJourney/components/UploadCard/utils/api/helpers';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../../../../app/hooks';
import { TabModule } from '../../../../../../utils/constants/commonConstants';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import FundingTable from './components/FundingTable';
import AskAicaQuestionBadge from '../../../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';

interface FundingHistoryData {
    fundingHistoryData: FundingHistoryRow[];
    debentureHistoryData: FundingHistoryRow[];
    lastUpdatedAt: string;
    taskTrackerData: any;
    totalFundingAmount: number;
    totalDebentureAmount: number;
}

type FundingHistoryRow = {
    allotmentDate: string;
    docId: string;
    fundingAmount: string;
    id: string;
    instrument: string;
    orgId: string;
    valuation: string;
    updatedAt: string;
    createdAt: string;
};

const headers = [
    { id: 'allotmentDate', label: 'Funding Date' },
    { id: 'fundingType', label: 'Funding Type' },
    { id: 'fundingAmount', label: 'Funding Amount' },
    // { id: 'valuation', label: 'Valuation' },
    { id: 'instrument', label: 'Instruments' },
    { id: 'docId', label: '' },
];

const getFundingHistoryHeaders = (isPDFReport: boolean) => {
    const filteredHeaders = headers.filter((header) => !isPDFReport || header.id !== 'docId');
    return filteredHeaders;
};

const AION_INGEST_FUNDING_HISTORY = 'AION_INGEST_FUNDING_HISTORY';

function FundingHistory({ isPDFReport = false }) {
    const MAX_POLLS = 10;
    const POLLING_TIME_INTERVAL_MS = 20000;
    const params = useParams();

    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);

    const [loading, setLoading] = useState(true);
    const [fundingHistory, setFundingHistory] = useState<null | FundingHistoryData>(null);
    const [taskTrackerStatus, setTaskTrackerStatus] = useState(null);

    const [fileDownloadingIndex, setFileDownloadingIndex] = useState<number | null>(null);

    const ingestingData = taskTrackerStatus === 'TODO' || taskTrackerStatus === 'PROCESSING' || taskTrackerStatus === 'NOT_AVAILABLE';
    const showFundingData = !ingestingData && !!fundingHistory?.fundingHistoryData?.length;
    const showDebentureData = !ingestingData && !!fundingHistory?.debentureHistoryData?.length;

    const pollingRef = useRef<any>(null);
    const pollingCountRef = useRef(0);

    useEffect(() => {
        fetchData();

        return () => {
            pollingRef.current && clearInterval(pollingRef.current);
        };
    }, []);

    useEffect(() => {
        if (modulePollingList.includes(TabModule.RELATED_PARTY) || modulePollingList.includes(TabModule.MCA_FILING)) {
            fetchData();
        }
    }, [modulePollingList]);

    const fetchData = (isPolling = false) => {
        if (!isPolling) setLoading(true);
        else pollingCountRef.current += 1;
        fetchFundingHistoryData({
            orgId: params.id as string,
            assesseeOrgId: params.id as string,
            taskStepsList: [AION_INGEST_FUNDING_HISTORY],
        })
            .then((res) => {
                const taskStatus = res?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status;
                setFundingHistory({
                    fundingHistoryData: res?.fundingHistoryData || [],
                    debentureHistoryData: res?.debentureHistoryData || [],
                    lastUpdatedAt: res?.lastUpdatedDate?.[0]?.lastUpdatedAt || '',
                    taskTrackerData: res?.taskTrackerData || {},
                    totalFundingAmount: res?.totalFundingAmount || 0,
                    totalDebentureAmount: res?.totalDebentureAmount || 0,
                });
                setTaskTrackerStatus(res?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status || 'NOT_AVAILABLE');

                // Start polling if: status is TODO/PROCESSING OR taskTrackerData is missing/undefined
                const shouldStartPolling =
                    ['TODO', 'PROCESSING'].includes(taskStatus) || !res?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY];

                if (shouldStartPolling && !pollingRef.current) {
                    pollingRef.current = setInterval(() => {
                        fetchData(true);
                    }, POLLING_TIME_INTERVAL_MS);
                } else if ((['COMPLETE', 'FAILED'].includes(taskStatus) && pollingRef.current) || pollingCountRef.current > MAX_POLLS) {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                }
            })
            .catch((err) => {
                console.error('Error in fetch funding history', err);
            })
            .finally(() => setLoading(false));
    };

    const downloadAttachment = (docId, rowIndex) => {
        setFileDownloadingIndex(rowIndex);
        downloadFile(Number(docId)).finally(() => {
            setFileDownloadingIndex(null);
        });
    };

    // if (['TODO', 'PROCESSING', 'NOT_AVAILABLE', null].includes(taskTrackerStatus)) return <></>;

    const getHeaderContent = () => {
        const lastUpdatedAt = fundingHistory?.lastUpdatedAt;
        const taskTrackerStatus = fundingHistory?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status;
        const dateCreated = fundingHistory?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.dateCreated;

        // CASE 1: Use lastUpdatedAt if available
        if (lastUpdatedAt && dayjs(lastUpdatedAt).isValid()) {
            return <div className={styles.lastUpdated}>Last updated on {dayjs(lastUpdatedAt).format('DD MMM YYYY')}</div>;
        }

        // CASE 3: Don't show date if status is FAILED
        if (taskTrackerStatus === 'FAILED') {
            return <div className={styles.statusMessage}>Not Able to Fetch</div>;
        }

        // CASE 2: Use dateCreated if lastUpdatedAt is null but status is COMPLETE
        if (!lastUpdatedAt && taskTrackerStatus === 'COMPLETE' && dateCreated) {
            return <div className={styles.lastUpdated}>Last updated on {dayjs(dateCreated).format('DD MMM YYYY')}</div>;
        }

        // Default: No date shown
        return null;
    };

    const shouldShowTable = () => {
        const taskTrackerStatus = fundingHistory?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status;
        return taskTrackerStatus !== 'FAILED' || showFundingData || showDebentureData;
    };

    const isEmpty = !ingestingData && !showFundingData && !showDebentureData;
    if (isEmpty && !isPDFReport) {
        return (
            <div className={styles.fundingHistory}>
                <div className={styles.emptyHeader}>
                    <h2 className={styles.heading}>Funding History</h2>
                </div>
            </div>
        );
    }

    const render = (
        <div className={styles.fundingHistory}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.heading}>Funding History</h2>
                </div>
                <div className={styles.headerRight}>
                    {!isPDFReport && !ingestingData && shouldShowTable() && (
                        <AskAicaQuestionBadge
                            question="Summarize the funding history"
                            prompt="Summarize the funding history of the company"
                        />
                    )}
                    {ingestingData ? (
                        <div className={styles.loaderMessage}>
                            <LoadingSpinner color="#6021b3" height="15px" />
                            <span>Fetching Data</span>
                        </div>
                    ) : (
                        getHeaderContent()
                    )}
                </div>
            </div>

            {!ingestingData && shouldShowTable() && (
                <div className={styles.content}>
                    <div className={styles.contentBody}>
                        <FundingTable
                            headers={getFundingHistoryHeaders(isPDFReport)}
                            fundingData={fundingHistory?.fundingHistoryData || []}
                            debentureData={fundingHistory?.debentureHistoryData || []}
                            totalFundingAmount={fundingHistory?.totalFundingAmount || 0}
                            totalDebentureAmount={fundingHistory?.totalDebentureAmount || 0}
                            isPDFReport={isPDFReport}
                            showFundingData={showFundingData}
                            showDebentureData={showDebentureData}
                            taskTrackerStatus={fundingHistory?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status || ''}
                            onDownload={downloadAttachment}
                            fileDownloadingIndex={fileDownloadingIndex}
                        />
                    </div>
                </div>
            )}
        </div>
    );

    if (isPDFReport)
        return reportSectionsToShow.includes(REPORT_SECTION_NAMES.FUNDING_HISTORY) && fundingHistory?.fundingHistoryData?.length ? (
            <A4Page sectionName={REPORT_SECTION_NAMES.FUNDING_HISTORY}>{render}</A4Page>
        ) : (
            <></>
        );
    else return render;
}

export default FundingHistory;
